import { memo, MouseEvent, useCallback, useEffect, useRef } from 'react';
import cx from 'classnames';
import _debounce from 'lodash/debounce';

import { displayInTwoDigitFormat } from 'utils';
import { useRender } from 'hooks';

import styles from './TimePicker.module.scss';

export type TimePickerProps = {
  defaultHour?: number;
  defaultMinute?: number;
  onChange?(hour: number, minute: number): void;
};

// source: https://css-tricks.com/practical-css-scroll-snapping/
const TimePicker = ({ defaultHour = 0, defaultMinute = 0, onChange }: TimePickerProps): JSX.Element => {
  const hoursRef = useRef<HTMLDivElement>(null);
  const minutesRef = useRef<HTMLDivElement>(null);
  const selectedRef = useRef<HTMLDivElement>(null);

  const selectedHour = useRef<number>(defaultHour);
  const selectedMinute = useRef<number>(defaultMinute);

  const { forceUpdate } = useRender();

  const handleOnChange = useCallback(() => {
    onChange?.(selectedHour.current, selectedMinute.current);
    forceUpdate?.();
  }, [onChange]);

  const handleOnChangeHour = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      event.currentTarget?.scrollIntoView({ block: 'center' });

      selectedHour.current = parseInt(event.currentTarget.getAttribute('data-id') as string, 10);

      handleOnChange?.();
    },
    [handleOnChange]
  );

  const handleOnChangeMinute = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      event.currentTarget?.scrollIntoView({ block: 'center' });

      selectedMinute.current = parseInt(event.currentTarget.getAttribute('data-id') as string, 10);

      handleOnChange?.();
    },
    [handleOnChange]
  );

  // set default hour scroll position based on default hour value effect
  useEffect(() => {
    const childrenList = hoursRef.current?.children && Array.from(hoursRef.current?.children);
    const target = selectedRef.current;

    if (target) {
      const targetValue = defaultHour;
      const selectedNode = childrenList?.find(
        childNode => childNode.getAttribute('data-id') === targetValue.toString()
      );

      if (selectedNode && hoursRef.current) {
        hoursRef.current.scrollTop = targetValue * 50;
        selectedHour.current = targetValue;
      }
    }
  }, [defaultHour]);

  // set default minute scroll position based on default minute value effect
  useEffect(() => {
    const childrenList = minutesRef.current?.children && Array.from(minutesRef.current?.children);
    const target = selectedRef.current;

    if (target) {
      const targetValue = defaultMinute;
      const selectedNode = childrenList?.find(
        childNode => childNode.getAttribute('data-id') === targetValue.toString()
      );

      if (selectedNode && minutesRef.current) {
        minutesRef.current.scrollTop = targetValue * 50;
        selectedMinute.current = targetValue;
      }
    }
  }, [defaultMinute]);

  // set hour based on scroll position effect
  useEffect(() => {
    const handleOnHourScrolled = _debounce((event: Event) => {
      if (hoursRef.current?.children && selectedRef.current) {
        const target = selectedRef.current;
        const childrenList = Array.from(hoursRef.current?.children);

        const selectedNode = childrenList.find(childNode => {
          const selectedRect = target.getBoundingClientRect();
          const targetRect = childNode.getBoundingClientRect();

          return Math.floor(selectedRect.top - targetRect.top) <= 1;
        });
        const attr = selectedNode?.getAttribute('data-id');

        if (attr) {
          selectedHour.current = parseInt(attr, 10);

          handleOnChange?.();
        }
      }
    }, 100);

    hoursRef.current?.addEventListener('scroll', handleOnHourScrolled);

    return () => {
      hoursRef.current?.removeEventListener('scroll', handleOnHourScrolled);
    };
  }, [handleOnChange]);

  // set minute based on scroll position effect
  useEffect(() => {
    const handleOnMinuteScrolled = _debounce(event => {
      if (minutesRef.current?.children && selectedRef.current) {
        const target = selectedRef.current;
        const childrenList = Array.from(minutesRef.current?.children);

        const selectedNode = childrenList.find(childNode => {
          const selectedRect = target.getBoundingClientRect();
          const targetRect = childNode.getBoundingClientRect();

          return Math.floor(selectedRect.top - targetRect.top) <= 1;
        });
        const attr = selectedNode?.getAttribute('data-id');

        if (attr) {
          selectedMinute.current = parseInt(attr, 10);

          handleOnChange?.();
        }
      }
    }, 100);

    minutesRef.current?.addEventListener('scroll', handleOnMinuteScrolled);

    return () => {
      minutesRef.current?.removeEventListener('scroll', handleOnMinuteScrolled);
    };
  }, [handleOnChange]);

  return (
    <div className={styles.container}>
      <div className={styles.title}>
        <p className={styles.hours}>Hour</p>
        <p className={styles.minutes}>Min</p>
      </div>
      <div className={styles.content}>
        <div ref={hoursRef} className={styles.scroll}>
          <div className={cx(styles.item, styles.empty)}>empty</div>
          {[...Array(24)]
            .map((_, hh) => hh)
            .map(hh => (
              <div key={hh} data-id={hh} aria-hidden="true" onClick={handleOnChangeHour}>
                <div className={cx(styles.item, { [styles.active]: hh === selectedHour.current })}>
                  {displayInTwoDigitFormat(hh)}
                </div>
              </div>
            ))}
          <div className={cx(styles.item, styles.empty)}>empty</div>
        </div>
        <div ref={selectedRef} className={styles.selected} />
        <div className={styles.twoDots}>:</div>
        <div ref={minutesRef} className={styles.scroll}>
          <div className={cx(styles.item, styles.empty)}>empty</div>
          {[...Array(60)]
            .map((_, index) => index)
            .map(mm => (
              <div key={mm} data-id={mm} aria-hidden="true" onClick={handleOnChangeMinute}>
                <div className={cx(styles.item, { [styles.active]: mm === selectedMinute.current })}>
                  {displayInTwoDigitFormat(mm as number)}
                </div>
              </div>
            ))}
          <div className={cx(styles.item, styles.empty)}>empty</div>
        </div>
      </div>
    </div>
  );
};

export default memo(TimePicker);
